/// <amd-module name="VSSUI/Components/LabelGroup/LabelGroup" />
import * as React from "react";
import { ILabelGroupProps, WrappingBehavior } from "./LabelGroup.Props";
import { ILabelModel } from "../Label/Label.Props";
import { Label } from "../Label/Label";
import { css, getSafeId } from "VSSUI/Util";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { IObservableValue, ObservableValue, IObservableArrayEventArgs, ObservableLike } from "VSS/Core/Observable";
import { IFocusWithinStatus, FocusWithin } from "VSSUI/FocusWithin";
import { IObservableExpression, Observer } from "VSSUI/Observer";

export class LabelGroup extends React.Component<ILabelGroupProps> {
    private contentSelectionMap: { [content: string]: IObservableValue<boolean> } = {};
    private labelReferences: Label[] = [];

    constructor(props: ILabelGroupProps) {
        super(props);

        this.buildContentSelectionMap(
            ObservableLike.getValue(props.labelProps),
            props.selectedLabelContents ? ObservableLike.getValue(props.selectedLabelContents) : []
        );
    }

    public focusLabel(index: number) {
        this.labelReferences[index] && this.labelReferences[index].focus();
    }

    public render() {
        const {
            className,
            defaultFocusElementId = "label-0",
            enableHoverStyles = false,
            fadeOutOverflow = false,
            id,
            labelProps,
            onLabelClick,
            onLabelKeyDown,
            selectedLabelContents,
            title,
            wrappingBehavior = WrappingBehavior.oneLine
        } = this.props;
        const wrappingClassName = wrappingBehavior == WrappingBehavior.oneLine ? "one-line" : "free-flow";

        return (
            <div className={css(className, "flex-column")}>
                {title && <div className="bolt-labelgroup-title-wrapper body-m">{title}</div>}
                <FocusZone
                    allowArrowOutOfInputs={true}
                    direction={FocusZoneDirection.Horizontal}
                    focusGroupProps={{ defaultElementId: defaultFocusElementId }}
                >
                    <div className={css("bolt-labelgroup flex-row", wrappingClassName, fadeOutOverflow && "fade-out")} id={getSafeId(id)}>
                        <FocusWithin>
                            {(focusWithinStatus: IFocusWithinStatus) => (
                                <>
                                    <Observer
                                        labelProps={
                                            { observableValue: labelProps, filter: this.onLabelsChanged } as IObservableExpression<ILabelModel[]>
                                        }
                                        selectedLabelContents={{ observableValue: selectedLabelContents, filter: this.onSelectedKeysChanged }}
                                    >
                                        {(observerProps: { labelProps: ILabelModel[] }) =>
                                            observerProps.labelProps &&
                                            observerProps.labelProps.map((model: ILabelModel, index: number) => (
                                                <Label
                                                    {...model}
                                                    enableHover={enableHoverStyles}
                                                    id={`label-${index}`}
                                                    key={model.content}
                                                    onBlur={focusWithinStatus.onBlur}
                                                    onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                        onLabelClick && onLabelClick(event, model, index)
                                                    }
                                                    onFocus={focusWithinStatus.onFocus}
                                                    onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) =>
                                                        onLabelKeyDown && onLabelKeyDown(event, model, index)
                                                    }
                                                    onMouseDown={(event: React.MouseEvent<HTMLDivElement>) =>
                                                        this.onLabelMouseDown(event, model, index)
                                                    }
                                                    ref={(label: Label) => (this.labelReferences[index] = label)}
                                                    selected={this.contentSelectionMap[model.content]}
                                                />
                                            ))
                                        }
                                    </Observer>

                                    {this.props.children}
                                </>
                            )}
                        </FocusWithin>
                    </div>
                </FocusZone>
            </div>
        );
    }

    private buildContentSelectionMap(labelProps: ILabelModel[], selectedLabels: string[]) {
        this.contentSelectionMap = {};

        const selectedSet: Set<string> = new Set<string>(selectedLabels);
        labelProps.forEach((model: ILabelModel) => {
            this.contentSelectionMap[model.content] = new ObservableValue<boolean>(selectedSet.has(model.content));
        });
    }

    private onLabelMouseDown = (event: React.MouseEvent<HTMLDivElement>, model: ILabelModel, index: number) => {
        const { disableMouseFocusOnLabels = true, onLabelMouseDown } = this.props;
        onLabelMouseDown && onLabelMouseDown(event, model, index);

        if (disableMouseFocusOnLabels) {
            event.preventDefault();
        }
    };

    private onLabelsChanged = (changes: IObservableArrayEventArgs<ILabelModel>) => {
        const selectedSet: Set<string> = new Set<string>(ObservableLike.getValue(this.props.selectedLabelContents));

        changes.removedItems &&
            changes.removedItems.forEach((model: ILabelModel) => {
                this.contentSelectionMap[model.content] = undefined;
            });

        changes.addedItems &&
            changes.addedItems.forEach((model: ILabelModel) => {
                this.contentSelectionMap[model.content] = new ObservableValue<boolean>(selectedSet.has(model.content));
            });

        return true;
    };

    private onSelectedKeysChanged = (newData: IObservableArrayEventArgs<string>) => {
        this.updateKeySelectionMap(newData.addedItems, newData.removedItems);
        return false;
    };

    private updateKeySelectionMap(newlySelectedLabels?: string[], noLongerSelectedLabels?: string[]) {
        newlySelectedLabels &&
            newlySelectedLabels.forEach((value: string) => {
                if (this.contentSelectionMap[value]) {
                    this.contentSelectionMap[value].value = true;
                }
            });

        noLongerSelectedLabels &&
            noLongerSelectedLabels.forEach((value: string) => {
                if (this.contentSelectionMap[value]) {
                    this.contentSelectionMap[value].value = false;
                }
            });
    }
}
